import { ChangeDetectionStrategy, Component, forwardRef, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgComponentOutlet } from '@angular/common';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { LucideChevronDown } from '@lucide/angular';
import { FieldBase } from '../shared/field-base';
import { FieldErrorComponent } from '../shared/field-error.component';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

/** Wraps a native <select> — the most familiar and assistive-tech-friendly picker. */
@Component({
  selector: 'ds-select',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgComponentOutlet, FieldErrorComponent, FormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectComponent),
      multi: true,
    },
  ],
  template: `
    <div class="ds-field">
      <label class="ds-field__label" [class.sr-only]="hideLabel()" [for]="fieldId">
        {{ label() }}
        @if (required()) {
          <span class="ds-field__required" aria-hidden="true">*</span>
        }
      </label>
      @if (helperText()) {
        <p class="ds-field__helper" [id]="fieldId + '-helper'">{{ helperText() }}</p>
      }
      <div class="select" [class.select--full]="fullWidth()">
        <select
          class="select__control"
          [id]="fieldId"
          [disabled]="disabled()"
          [required]="required()"
          [attr.aria-invalid]="errorText() ? 'true' : null"
          [attr.aria-describedby]="describedBy()"
          (change)="handleChange($event)"
          (blur)="onTouched()"
        >
          <option value="" [selected]="!value()" disabled hidden>{{ placeholder() }}</option>
          @for (option of options(); track option.value) {
            <option
              [value]="option.value"
              [selected]="option.value === value()"
              [disabled]="option.disabled ?? false"
            >
              {{ option.label }}
            </option>
          }
        </select>
        <span class="select__chevron" aria-hidden="true">
          <ng-container
            [ngComponentOutlet]="chevron"
            [ngComponentOutletInputs]="{ size: 16, strokeWidth: 2.25 }"
          />
        </span>
      </div>
      @if (errorText()) {
        <ds-field-error [errorId]="fieldId + '-error'">{{ errorText() }}</ds-field-error>
      }
    </div>
  `,
  styles: `
    .select {
      position: relative;
      display: inline-grid;
      width: fit-content;

      &--full {
        display: grid;
        width: 100%;
      }
    }

    .select__control {
      appearance: none;
      width: 100%;
      padding: var(--space-2) var(--space-6) var(--space-2) var(--space-3);
      background: var(--color-surface);
      border: 1.5px solid var(--color-border-subtle);
      border-radius: var(--radius-md);
      font-size: var(--text-sm);
      font-weight: var(--font-weight-medium);
      color: var(--color-text);
      cursor: pointer;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      transition: border-color var(--motion-fast);

      &:hover:not(:disabled) {
        border-color: var(--color-primary);
      }

      &:focus-visible {
        outline: 2px solid var(--color-primary);
        outline-offset: 2px;
      }

      &:disabled {
        cursor: not-allowed;
        opacity: 0.6;
      }
    }

    .select__chevron {
      position: absolute;
      right: var(--space-3);
      top: 50%;
      transform: translateY(-50%);
      display: inline-flex;
      pointer-events: none;
      color: var(--color-text-secondary);
    }

    .sr-only {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    }
  `,
})
export class SelectComponent extends FieldBase<string> {
  options = input.required<SelectOption[]>();
  placeholder = input('Silakan pilih');
  /** When true, the select takes full width of its container. */
  fullWidth = input(false);
  /** When true, the label is visually hidden but still announced by assistive tech. */
  hideLabel = input(false);
  /** Emits when selection changes. */
  readonly change = output<string>();

  protected readonly chevron = LucideChevronDown;

  protected handleChange(event: Event): void {
    const next = (event.target as HTMLSelectElement).value;
    this.writeValue(next);
    this.onChange(next);
    this.change.emit(next);
  }
}
